import { create } from 'zustand';
import i18n from '../i18n';

// API
import { getTyreDetailsApi, listTyresApi } from '../api/endpoints';

const useTyreStore = create((set, get) => ({
  // State
  tyreData: null,
  priceData: null,
  loading: true,
  error: null,
  activeSection: 'header',
  currentEan: null,

  // Actions
  setActiveSection: (section) => set({ activeSection: section }),

  reset: () =>
    set({
      tyreData: null,
      priceData: null,
      loading: true,
      error: null,
      activeSection: 'header',
      currentEan: null,
    }),

  fetchTyreDetails: async (ean, language = null) => {
    const lang = language || i18n.language || 'en';
    // Avoid refetching if same EAN
    if (get().currentEan === ean && get().tyreData) {
      return;
    }

    set({ loading: true, error: null, currentEan: ean });

    try {
      const data = await getTyreDetailsApi(ean, lang);
      const tyreData = data?.getProductLineListing?.edges?.[0]?.node;

      if (!tyreData) {
        throw new Error('No tyre data found');
      }

      set({ tyreData, loading: false });

      // Fetch price data after tyre data is loaded
      if (tyreData.ean) {
        get().fetchPriceData(tyreData.ean);
      }
    } catch (error) {
      console.error('Error fetching tyre details:', error);
      set({ error: error.message, loading: false });
    }
  },

  fetchPriceData: async (ean) => {
    if (!ean) {
      console.error('No EAN provided for price fetch');
      return;
    }

    try {
      const response = await listTyresApi({
        ean__icontains: ean,
        limit: 1,
      });

      if (response?.[0]) {
        set({
          priceData: {
            grossPrice: response[0].grossPrice,
            netPrice: response[0].netPrice,
            discountGrossPrice: response[0].discountGrossPrice,
            depth: response[0].depth ?? response[0].treadDepth,
          },
        });
      }
    } catch (error) {
      console.error('Error fetching price data:', error);
      set({ priceData: null });
    }
  },

  // Prepare data for components - extracted from TyreContainer
  getPreparedData: () => {
    const { tyreData, priceData } = get();
    if (!tyreData) return null;

    const customerBenefits = [
      tyreData.bulletpointsExtern1_approved,
      tyreData.bulletpointsExtern2_approved,
      tyreData.bulletpointsExtern3_approved,
      tyreData.bulletpointsExtern4_approved,
      tyreData.bulletpointsExtern5_approved,
      tyreData.bulletpointsExtern6_approved,
    ].filter((item) => item && item.trim().length > 0);

    const businessPartnerBenefits = [
      tyreData.bulletpointsIntern1_approved,
      tyreData.bulletpointsIntern2_approved,
      tyreData.bulletpointsIntern3_approved,
      tyreData.bulletpointsIntern4_approved,
      tyreData.bulletpointsIntern5_approved,
      tyreData.bulletpointsIntern6_approved,
    ].filter((item) => item && item.trim().length > 0);

    const technicalHighlightsB2C = [
      tyreData.techHighlightsDescriptionsB2C1_approved,
      tyreData.techHighlightsDescriptionsB2C2_approved,
      tyreData.techHighlightsDescriptionsB2C3_approved,
      tyreData.techHighlightsDescriptionsB2C4_approved,
      tyreData.techHighlightsDescriptionsB2C5_approved,
      tyreData.techHighlightsDescriptionsB2C6_approved,
    ].filter((item) => item && item.trim().length > 0);

    const technicalHighlightsB2B = [
      tyreData.techHighlightsDescriptionsB2B1_approved,
      tyreData.techHighlightsDescriptionsB2B2_approved,
      tyreData.techHighlightsDescriptionsB2B3_approved,
      tyreData.techHighlightsDescriptionsB2B4_approved,
      tyreData.techHighlightsDescriptionsB2B5_approved,
      tyreData.techHighlightsDescriptionsB2B6_approved,
    ].filter((item) => item && item.trim().length > 0);

    const specs = {
      ean: tyreData.ean,
      dimensionMetric: tyreData.dimensionMetric,
      aspectRatio: tyreData.aspectRatio,
      rimDiameterInch: tyreData.rimDiameterInch,
      rimDiameterMm: tyreData.rimDiameterMm,
      rimWidth: tyreData.rimWidth,
      loadSpeedIndex: tyreData.loadSpeedIndex,
      loadCapacity: tyreData.loadCapacity,
      maxSpeed: tyreData.maxSpeed,
      maxLoad: tyreData.maxLoad,
      season: tyreData.season,
      sidewallDesign: tyreData.sidewallDesign,
      snowflakeSymbol: tyreData.snowflakeSymbol,
      mSMark: tyreData.mSMark,
      silent: tyreData.silent,
      zz_elec_veh: tyreData.zz_elec_veh,
      treadDepth: tyreData.treadDepth ?? priceData?.depth,
      productionOrigin: tyreData.productionOrigin?.name,
      vehicleType: tyreData.vehicleType?.name,
      application: tyreData.application?.name,
      approvedRims: tyreData.approvedRims,
      oeIndicator: tyreData.oeIndicator,
      oeManufacture: tyreData.oeManufacture,
    };

    const euLabel = {
      FUEL_EFFI: tyreData.FUEL_EFFI,
      FUEL_EFFI_E2: tyreData.FUEL_EFFI_E2,
      WET_GRIP: tyreData.WET_GRIP,
      WET_GRIP_E2: tyreData.WET_GRIP_E2,
      ROLL_NOISE: tyreData.ROLL_NOISE,
      ROLL_NOISE_E2: tyreData.ROLL_NOISE_E2,
      NOISE_UNIT: tyreData.NOISE_UNIT,
      NOISE_UNIT_E2: tyreData.NOISE_UNIT_E2,
      NOISE_LEV: tyreData.NOISE_LEV,
      NOISE_LEV_E2: tyreData.NOISE_LEV_E2,
      TIRE_CLASS: tyreData.TIRE_CLASS,
      TIRE_CLASS_E2: tyreData.TIRE_CLASS_E2,
      E2_3PMSF: tyreData.E2_3PMSF,
      ICE_PICT_E2: tyreData.ICE_PICT_E2,
      EPREL_REG_E2: tyreData.EPREL_REG_E2,
      REG_REF: tyreData.REG_REF,
      REG_REF_E2: tyreData.REG_REF_E2,
      microsite_eulabel_link: tyreData.microsite_eulabel_link,
    };

    const media = {
      frontView: tyreData.frontView?.fullpath,
      cutNoRim: tyreData.cutNoRim?.fullpath,
      noRim: tyreData.noRim?.fullpath,
      additionalVisuals:
        tyreData.additionalVisuals
          ?.map((v) => v.element?.fullpath)
          .filter(Boolean) || [],
      keyVisuals:
        tyreData.keyVisuals?.map((v) => v.element?.fullpath).filter(Boolean) ||
        [],
      productVideo:
        tyreData.productVideo
          ?.map((v) => v.element?.fullpath)
          .filter(Boolean) || [],
      microsite_b2b_link: tyreData.microsite_b2b_link,
      microsite_productline_link: tyreData.microsite_productline_link,
    };

    return {
      customerBenefits,
      businessPartnerBenefits,
      technicalHighlightsB2C,
      technicalHighlightsB2B,
      specs,
      euLabel,
      media,
    };
  },
}));

export default useTyreStore;




